import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useUser } from '@/contexts/UserContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { filterShipmentsByWarehouse, filterByWarehouse, filterInvoicesByWarehouse } from '@/lib/warehouseFilter';
import { useCustomers } from '@/hooks/useCustomers';
import { useWarehouses } from '@/hooks/useWarehouses';
import { useShipments } from '@/hooks/useShipments';
import { useTrips } from '@/hooks/useTrips';
import { useInvoices } from '@/hooks/useInvoices';
import {
  Users,
  Building2,
  Package,
  Truck,
  FileText,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  BarChart3,
  PieChart,
  Target,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Printer,
  Calendar,
  Filter
} from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function Dashboard() {
  const { user } = useUser();
  const { t } = useLanguage();

  const { customers: apiCustomers } = useCustomers();
  const { warehouses: apiWarehouses } = useWarehouses();
  const { shipments: apiShipments } = useShipments();
  const { trips: apiTrips } = useTrips();
  const { invoices: rawInvoices } = useInvoices();
  
  // Map invoice fields from snake_case to camelCase (same as Invoices page)
  const apiInvoices = (rawInvoices || []).map((inv: any) => ({
    ...inv,
    destinationWarehouse: inv.destination_warehouse,
    customerCode: inv.customer_code,
    customerName: inv.customer_name,
    invoiceNumber: inv.invoice_number,
    total: parseFloat(inv.total) || 0,
  }));

  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalWarehouses: 0,
    totalShipments: 0,
    activeTrips: 0,
    pendingInvoices: 0,
    totalRevenue: 0,
    revenueByWarehouse: {} as Record<string, number>,
    shipmentsByStatus: {
      Processing: 0,
      'In Transit': 0,
      'At Destination': 0,
      Delivered: 0
    },
    tripsByStatus: {
      'Planning': 0,
      'In Transit': 0,
      'At Destination': 0,
      'Completed': 0
    },
    invoicesByStatus: {
      'Draft': 0,
      'Sent': 0,
      'Paid': 0,
      'Overdue': 0
    },
    warehousesByType: {
      'Main': 0,
      'Branch': 0,
      'Distribution': 0
    },
    warehousesByStatus: {
      'Active': 0,
      'Inactive': 0,
      'Maintenance': 0
    },
    customersByStatus: {
      'Active': 0,
      'Inactive': 0
    }
  });

  const [recentShipments, setRecentShipments] = useState<any[]>([]);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [sectionData, setSectionData] = useState<any[]>([]);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [showFilters, setShowFilters] = useState(false);

  // Function to calculate real dashboard stats
  const calculateDashboardStats = () => {
    // Use API data instead of localStorage
    const warehouses = apiWarehouses || [];
    const allCustomers = apiCustomers || [];
    const allShipments = apiShipments || [];
    const allTrips = apiTrips || [];
    const allInvoices = apiInvoices || [];

    // Filter customers for warehouse users
    const customers = user?.role === 'warehouse'
      ? allCustomers.filter((customer: any) => {
          // Only show customers assigned to this warehouse (check both field name formats)
          const customerWarehouse = customer.destination_warehouse || customer.destinationWarehouse;
          const destinationMatch = customerWarehouse === user.warehouseCode;
          return destinationMatch;
        })
      : allCustomers;

    // Filter shipments
    const shipments = filterByWarehouse(allShipments, user);

    // Update customer shipment counts
    const customersWithUpdatedShipments = customers.map((customer: any) => {
      const customerShipments = shipments.filter((s: any) =>
        s.customer_code === customer.code || s.customerCode === customer.code
      );
      return {
        ...customer,
        totalShipments: customerShipments.length
      };
    });

    // Filter trips
    const trips = filterByWarehouse(allTrips, user);

    // Filter invoices (use same filter as Invoices page for consistency)
    const invoices = filterByWarehouse(allInvoices, user);

    // Calculate shipments by status
    const shipmentsByStatus = {
      Processing: shipments.filter((s: any) => s.status === 'Processing').length,
      'In Transit': shipments.filter((s: any) => s.status === 'In Transit').length,
      'At Destination': shipments.filter((s: any) => s.status === 'At Destination').length,
      Delivered: shipments.filter((s: any) => s.status === 'Delivered').length
    };

    // Calculate trips by status
    const tripsByStatus = {
      Planning: trips.filter((t: any) => t.status === 'Planning').length,
      'In Transit': trips.filter((t: any) => t.status === 'In Transit').length,
      'At Destination': trips.filter((t: any) => t.status === 'At Destination').length,
      Completed: trips.filter((t: any) => t.status === 'Completed').length
    };

    // Calculate invoices by status
    const invoicesByStatus = {
      Draft: invoices.filter((i: any) => i.status === 'Draft').length,
      Sent: invoices.filter((i: any) => i.status === 'Sent').length,
      'Paid': invoices.filter((i: any) => i.status === 'Paid').length,
      'Overdue': invoices.filter((i: any) => i.status === 'Overdue').length
    };

    // Calculate warehouses by type
    const warehousesByType = {
      'Main': warehouses.filter((w: any) => w.type === 'Main').length,
      'Branch': warehouses.filter((w: any) => w.type === 'Branch').length,
      'Distribution': warehouses.filter((w: any) => w.type === 'Distribution').length
    };

    // Calculate warehouses by status
    const warehousesByStatus = {
      'Active': warehouses.filter((w: any) => w.status === 'Active').length,
      'Inactive': warehouses.filter((w: any) => w.status === 'Inactive').length,
      'Maintenance': warehouses.filter((w: any) => w.status === 'Maintenance').length
    };

    // Calculate customers by status
    const customersByStatus = {
      'Active': customersWithUpdatedShipments.filter((c: any) => c.status === 'Active').length,
      'Inactive': customersWithUpdatedShipments.filter((c: any) => c.status === 'Inactive').length
    };

    // Calculate stats
    const activeShipments = shipments.filter((s: any) => s.status !== 'Delivered').length;
    const activeTrips = trips.filter((t: any) => t.status !== 'Completed').length;
    const pendingInvoices = invoices.filter((i: any) => i.status !== 'Paid').length;

    // Calculate total revenue and revenue by warehouse
    // Include both fully paid and partially paid invoices
    const paidInvoices = invoices.filter((i: any) => i.status === 'Paid' || i.status === 'Partial');
    const totalRevenue = paidInvoices.reduce((sum: number, invoice: any) => {
      // For partial payments, use amount_paid instead of total
      const amount = invoice.status === 'Partial' 
        ? (parseFloat(invoice.amount_paid || invoice.amountPaid) || 0)
        : (parseFloat(invoice.total) || 0);
      return sum + amount;
    }, 0);

    // Calculate revenue by warehouse (show for both admin and warehouse users)
    const revenueByWarehouse: Record<string, number> = {};
    paidInvoices.forEach((invoice: any) => {
      const warehouse = invoice.destination_warehouse || invoice.destinationWarehouse || 'Unknown';
      // For partial payments, use amount_paid instead of total
      const amount = invoice.status === 'Partial' 
        ? (parseFloat(invoice.amount_paid || invoice.amountPaid) || 0)
        : (parseFloat(invoice.total) || 0);
      revenueByWarehouse[warehouse] = (revenueByWarehouse[warehouse] || 0) + amount;
    });

    // Get recent shipments (last 5)
    const recent = shipments
      .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5)
      .map((shipment: any) => ({
        id: shipment.shipmentNumber,
        customer: shipment.customerName,
        destination: shipment.destinationWarehouse,
        status: shipment.status,
        date: shipment.createdAt
      }));

    setStats({
      totalCustomers: customersWithUpdatedShipments.length,
      totalWarehouses: warehouses.length,
      totalShipments: shipments.length,
      activeTrips,
      pendingInvoices,
      totalRevenue,
      revenueByWarehouse,
      shipmentsByStatus,
      tripsByStatus,
      invoicesByStatus,
      warehousesByType,
      warehousesByStatus,
      customersByStatus
    });

    setRecentShipments(recent);
  };

// Calculate stats on component mount and update periodically
  useEffect(() => {
    calculateDashboardStats();
  }, [apiCustomers, apiWarehouses, apiShipments, apiTrips, apiInvoices]);

  const getStatusTranslation = (status: string): string => {
    const map: Record<string, string> = {
      'Processing': t('processing'),
      'In Transit': t('inTransit'),
      'At Destination': t('atDestination'),
      'Delivered': t('delivered'),
      'Planning': t('planning'),
      'Completed': t('completed'),
      'Draft': t('draft'),
      'Sent': t('sent'),
      'Paid': t('paid'),
      'Overdue': t('overdue'),
      'Partial': t('partial'),
      'Active': t('active'),
      'Inactive': t('inactive'),
      'Maintenance': t('maintenance'),
    };
    return map[status] || status;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Delivered': return 'text-green-600 bg-green-100';
      case 'In Transit': return 'text-blue-600 bg-blue-100';
      case 'Processing': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Delivered': return <CheckCircle className="h-4 w-4" />;
      case 'In Transit': return <Truck className="h-4 w-4" />;
      case 'Processing': return <Clock className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  // Handle status click to show specific section
  const handleStatusClick = (status: string, sectionType: string = 'shipments') => {
    let filteredData: any[] = [];

    switch (sectionType) {
      case 'shipments':
        const shipments = filterByWarehouse(apiShipments || [], user);
        filteredData = shipments
          .filter((s: any) => s.status === status)
          .sort((a: any, b: any) => new Date(b.created_at || b.createdAt).getTime() - new Date(a.created_at || a.createdAt).getTime())
          .map((shipment: any) => ({
            id: shipment.shipment_number || shipment.shipmentNumber,
            customer: shipment.customer_name || shipment.customerName,
            customerCode: shipment.customer_code || shipment.customerCode,
            destination: shipment.destination_warehouse || shipment.destinationWarehouse,
            weight: shipment.weight,
            volume: shipment.volume,
            packages: shipment.packages,
            freightMethod: shipment.freight_method || shipment.freightMethod,
            tripName: shipment.trip_name || shipment.tripName,
            status: shipment.status,
            createdAt: shipment.created_at || shipment.createdAt,
            estimatedDelivery: shipment.estimated_delivery || shipment.estimatedDelivery
          }));
        break;

      case 'trips':
        const trips = filterByWarehouse(apiTrips || [], user);
        filteredData = trips
          .filter((t: any) => t.status === status)
          .sort((a: any, b: any) => new Date(b.created_at || b.createdAt).getTime() - new Date(a.created_at || a.createdAt).getTime())
          .map((trip: any) => ({
            id: trip.tripNumber,
            driver: trip.driver,
            vehicle: trip.vehicle,
            origin: trip.originWarehouse,
            destination: trip.destinationWarehouse,
            status: trip.status,
            createdAt: trip.createdAt,
            estimatedArrival: trip.estimatedArrival,
            totalShipments: trip.totalShipments,
            totalWeight: trip.totalWeight,
            totalVolume: trip.totalVolume,
            totalPackages: trip.totalPackages
          }));
        break;

      case 'invoices':
        const invoices = filterByWarehouse(apiInvoices || [], user);
        filteredData = invoices
          .filter((i: any) => i.status === status)
          .sort((a: any, b: any) => new Date(b.created_at || b.createdAt).getTime() - new Date(a.created_at || a.createdAt).getTime())
          .map((invoice: any) => ({
            id: invoice.invoice_number || invoice.invoiceNumber,
            customer: invoice.customer_name || invoice.customerName,
            amount: parseFloat(invoice.total) || 0,
            status: invoice.status,
            dueDate: invoice.due_date || invoice.dueDate,
            createdAt: invoice.created_at || invoice.createdAt,
            shipments: invoice.shipments || []
          }));
        break;

      case 'warehouses':
        const warehousesFiltered = user?.role === 'warehouse'
          ? (apiWarehouses || []).filter((warehouse: any) => warehouse.code === user.warehouseCode)
          : (apiWarehouses || []);

        filteredData = warehousesFiltered
          .filter((w: any) => w.status === status)
          .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
          .map((warehouse: any) => ({
            id: warehouse.code,
            name: warehouse.name,
            location: warehouse.location,
            address: warehouse.address,
            city: warehouse.city,
            state: warehouse.state,
            country: warehouse.country,
            phone: warehouse.phone,
            manager: warehouse.manager,
            capacity: warehouse.capacity,
            currentStock: warehouse.currentStock,
            status: warehouse.status,
            type: warehouse.type,
            createdAt: warehouse.createdAt
          }));
        break;

      case 'customers':
        const customersFiltered = user?.role === 'warehouse'
          ? (apiCustomers || []).filter((customer: any) => {
              // Only show customers assigned to this warehouse (check both field name formats)
              const customerWarehouse = customer.destination_warehouse || customer.destinationWarehouse;
              const destinationMatch = customerWarehouse === user.warehouseCode;
              return destinationMatch;
            })
          : (apiCustomers || []);

        const shipmentsForCustomers = filterByWarehouse(apiShipments || [], user);
        const customersWithShipmentCount = customersFiltered.map((customer: any) => {
          const customerShipments = shipmentsForCustomers.filter((s: any) =>
            s.customer_code === customer.code || s.customerCode === customer.code
          );
          return {
            ...customer,
            totalShipments: customerShipments.length
          };
        });

        filteredData = customersWithShipmentCount
          .filter((c: any) => c.status === status)
          .sort((a: any, b: any) => new Date(b.created_at || b.createdAt).getTime() - new Date(a.created_at || a.createdAt).getTime())
          .map((customer: any) => ({
            id: customer.code,
            name: customer.name,
            email: customer.email,
            phone: customer.phone,
            city: customer.city,
            country: customer.country,
            totalShipments: customer.totalShipments || 0,
            status: customer.status,
            createdAt: customer.createdAt
          }));
        break;
    }

    setActiveSection(`${sectionType}-${status}`);
    setSectionData(filteredData);
  };

  // Handle navigation to other sections
  const handleNavigationClick = (section: string) => {
    setActiveSection(section);

    switch (section) {
      case 'customers':
        const customersNav = user?.role === 'warehouse'
          ? (apiCustomers || []).filter((customer: any) => {
              // Only show customers assigned to this warehouse (check both field name formats)
              const customerWarehouse = customer.destination_warehouse || customer.destinationWarehouse;
              const destinationMatch = customerWarehouse === user.warehouseCode;
              return destinationMatch;
            })
          : (apiCustomers || []);

        const shipmentsForNav = filterByWarehouse(apiShipments || [], user);
        const customersWithUpdatedShipmentsNav = customersNav.map((customer: any) => {
          const customerShipments = shipmentsForNav.filter((s: any) =>
            (s.customer_code || s.customerCode) === customer.code
          );
          return {
            ...customer,
            totalShipments: customerShipments.length
          };
        });

        setSectionData(customersWithUpdatedShipmentsNav.map((customer: any) => ({
          id: customer.code,
          name: customer.name,
          email: customer.email,
          phone: customer.phone,
          city: customer.city,
          country: customer.country,
          totalShipments: customer.totalShipments || 0,
          status: customer.status,
          createdAt: customer.createdAt
        })));
        break;

      case 'warehouses':
        const warehousesNav = user?.role === 'warehouse'
          ? (apiWarehouses || []).filter((warehouse: any) => warehouse.code === user.warehouseCode)
          : (apiWarehouses || []);

        setSectionData(warehousesNav.map((warehouse: any) => ({
          id: warehouse.code,
          name: warehouse.name,
          location: warehouse.location,
          address: warehouse.address,
          city: warehouse.city,
          state: warehouse.state,
          country: warehouse.country,
          phone: warehouse.phone,
          manager: warehouse.manager,
          capacity: warehouse.capacity,
          currentStock: warehouse.current_stock || warehouse.currentStock,
          status: warehouse.status,
          type: warehouse.type,
          createdAt: warehouse.created_at || warehouse.createdAt
        })));
        break;

      case 'invoices':
        const invoicesNav = filterByWarehouse(apiInvoices || [], user);
        setSectionData(invoicesNav.map((invoice: any) => ({
          id: invoice.invoice_number || invoice.invoiceNumber,
          customer: invoice.customer_name || invoice.customerName,
          amount: invoice.total,
          status: invoice.status,
          dueDate: invoice.dueDate,
          createdAt: invoice.createdAt
        })));
        break;

      default:
        setSectionData([]);
    }
  };

  // Get section title and icon
  const getSectionInfo = (section: string) => {
    const [sectionType, status] = section.split('-');

    switch (sectionType) {
      case 'shipments':
        switch (status) {
          case 'Processing':
            return { title: t('processingShipments'), icon: <Clock className="h-5 w-5" /> };
          case 'In Transit':
            return { title: t('shipmentsInTransit'), icon: <Truck className="h-5 w-5" /> };
          case 'At Destination':
            return { title: t('shipmentsAtDestination'), icon: <Target className="h-5 w-5" /> };
          case 'Delivered':
            return { title: t('deliveredShipments'), icon: <CheckCircle className="h-5 w-5" /> };
        }
        break;
      case 'trips':
        return { title: `${t(status.toLowerCase())} ${t('tripsLabel')}`, icon: <Truck className="h-5 w-5" /> };
      case 'invoices':
        return { title: `${t(status.toLowerCase())} ${t('invoicesLabel')}`, icon: <FileText className="h-5 w-5" /> };
      case 'warehouses':
        return { title: `${t(status.toLowerCase())} ${t('warehousesLabel')}`, icon: <Building2 className="h-5 w-5" /> };
      case 'customers':
        return { title: `${t(status?.toLowerCase() || 'all')} ${t('customersLabel')}`, icon: <Users className="h-5 w-5" /> };
    }

    return { title: t('sectionDetails'), icon: <Package className="h-5 w-5" /> };
  };

  // Export to PDF function
  const exportToPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Dashboard Report - ${new Date().toLocaleDateString()}</title>
        <style>
          @page {
            size: A4;
            margin: 15mm;
          }
          
          @media print {
            body { 
              padding: 0;
              margin: 0;
            }
          }
          
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            line-height: 1.6;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #2563eb;
            padding-bottom: 20px;
          }
          h1 {
            color: #2563eb;
            margin: 0;
          }
          .stats-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 20px;
            margin-bottom: 30px;
          }
          .stat-card {
            border: 1px solid #e5e7eb;
            padding: 15px;
            border-radius: 8px;
          }
          .stat-title {
            font-size: 14px;
            color: #6b7280;
            margin-bottom: 10px;
          }
          .stat-value {
            font-size: 32px;
            font-weight: bold;
            color: #111827;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 12px;
            color: #6b7280;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Dashboard Report</h1>
          <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
        </div>
        
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-title">Total Shipments</div>
            <div class="stat-value">${stats.totalShipments}</div>
          </div>
          <div class="stat-card">
            <div class="stat-title">Total Customers</div>
            <div class="stat-value">${stats.totalCustomers}</div>
          </div>
          <div class="stat-card">
            <div class="stat-title">Total Warehouses</div>
            <div class="stat-value">${stats.totalWarehouses}</div>
          </div>
          <div class="stat-card">
            <div class="stat-title">Total Trips</div>
            <div class="stat-value">${stats.totalTrips}</div>
          </div>
          <div class="stat-card">
            <div class="stat-title">Total Invoices</div>
            <div class="stat-value">${stats.totalInvoices}</div>
          </div>
          <div class="stat-card">
            <div class="stat-title">Total Revenue</div>
            <div class="stat-value">$${stats.totalRevenue.toFixed(2)}</div>
          </div>
        </div>
        
        <div class="footer">
          SourceMyGoods - Professional Sourcing, Inspection & Global Shipping
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Export to Excel (CSV) function
  const exportToExcel = () => {
    const csvData = [
      ['SourceMyGoods Dashboard Report'],
      [`Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`],
      [],
      ['Metric', 'Value'],
      ['Total Shipments', stats.totalShipments],
      ['Total Customers', stats.totalCustomers],
      ['Total Warehouses', stats.totalWarehouses],
      ['Total Trips', stats.totalTrips],
      ['Total Invoices', stats.totalInvoices],
      ['Total Revenue', `$${stats.totalRevenue.toFixed(2)}`],
      [],
      ['Shipments by Status'],
      ['Processing', stats.shipmentsByStatus.Processing],
      ['In Transit', stats.shipmentsByStatus['In Transit']],
      ['At Destination', stats.shipmentsByStatus['At Destination']],
      ['Delivered', stats.shipmentsByStatus.Delivered],
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `dashboard-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('dashboardTitle')}</h1>
            <p className="text-gray-600">{t('overviewOfOperations')}</p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              {t('filters')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportToPDF}
              className="flex items-center gap-2"
            >
              <Printer className="h-4 w-4" />
              {t('exportPdf')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportToExcel}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {t('exportExcel')}
            </Button>
          </div>
        </div>

        {/* Date Range Filter */}
        {showFilters && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('dateRangeFilter')}</CardTitle>
              <CardDescription>{t('filterDashboardByDate')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                   <Label htmlFor="startDate">{t('startDate')}</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                   <Label htmlFor="endDate">{t('endDate')}</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  />
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button
                  size="sm"
                  onClick={() => {
                    // Recalculate stats with date filter
                    calculateDashboardStats();
                  }}
                >
                  {t('applyFilter')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setDateRange({ startDate: '', endDate: '' });
                    calculateDashboardStats();
                  }}
                >
                  {t('clearFilter')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Shipments Card */}
          <Card className="cursor-pointer hover:shadow-lg transition-all border-t-4 border-t-blue-500 hover:scale-105 duration-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">{t('totalShipments')}</CardTitle>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats.totalShipments}</div>
              <p className="text-xs text-gray-500 mb-4 flex items-center gap-1">
                <ArrowUpRight className="h-3 w-3 text-green-600" />
                {t('activeShipmentsInSystem')}
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm cursor-pointer hover:text-yellow-600" onClick={() => handleStatusClick('Processing')}>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3 text-yellow-600" />
                    {t('processing')}
                  </span>
                  <span className="font-medium">{stats.shipmentsByStatus.Processing}</span>
                </div>
                <div className="flex items-center justify-between text-sm cursor-pointer hover:text-blue-600" onClick={() => handleStatusClick('In Transit')}>
                  <span className="flex items-center gap-1">
                    <Truck className="h-3 w-3 text-blue-600" />
                    {t('inTransit')}
                  </span>
                  <span className="font-medium">{stats.shipmentsByStatus['In Transit']}</span>
                </div>
                <div className="flex items-center justify-between text-sm cursor-pointer hover:text-purple-600" onClick={() => handleStatusClick('At Destination')}>
                  <span className="flex items-center gap-1">
                    <Target className="h-3 w-3 text-purple-600" />
                    {t('atDestination')}
                  </span>
                  <span className="font-medium">{stats.shipmentsByStatus['At Destination']}</span>
                </div>
                <div className="flex items-center justify-between text-sm cursor-pointer hover:text-green-600" onClick={() => handleStatusClick('Delivered')}>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-green-600" />
                    {t('delivered')}
                  </span>
                  <span className="font-medium">{stats.shipmentsByStatus.Delivered}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customers Card */}
          <Card className="cursor-pointer hover:shadow-lg transition-all border-t-4 border-t-purple-500 hover:scale-105 duration-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">{t('totalCustomers')}</CardTitle>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats.totalCustomers}</div>
              <p className="text-xs text-gray-500 mb-4 flex items-center gap-1">
                <Users className="h-3 w-3 text-purple-600" />
                {t('registeredCustomerAccounts')}
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm cursor-pointer hover:text-green-600" onClick={() => handleStatusClick('Active', 'customers')}>
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3 text-green-600" />
                    {t('active')}
                  </span>
                  <span className="font-medium">{stats.customersByStatus.Active}</span>
                </div>
                <div className="flex items-center justify-between text-sm cursor-pointer hover:text-gray-600" onClick={() => handleStatusClick('Inactive', 'customers')}>
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3 text-gray-600" />
                    {t('inactive')}
                  </span>
                  <span className="font-medium">{stats.customersByStatus.Inactive}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Warehouses Card - Only shown to admin users */}
          {user?.role === 'admin' && (
            <Card className="cursor-pointer hover:shadow-lg transition-all border-t-4 border-t-orange-500 hover:scale-105 duration-200">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('warehouses')}</CardTitle>
                <Building2 className="h-4 w-4 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-700">
                  {user?.role === 'warehouse' ? 1 : stats.totalWarehouses}
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  {user?.role === 'warehouse' ? t('yourWarehouseSingle') : t('totalWarehousesLabel')}
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm cursor-pointer hover:text-green-600" onClick={() => handleStatusClick('Active', 'warehouses')}>
                    <span className="flex items-center gap-1">
                      <Building2 className="h-3 w-3 text-green-600" />
                       {t('active')}
                    </span>
                    <span className="font-medium">{stats.warehousesByStatus.Active}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm cursor-pointer hover:text-gray-600" onClick={() => handleStatusClick('Inactive', 'warehouses')}>
                    <span className="flex items-center gap-1">
                      <Building2 className="h-3 w-3 text-gray-600" />
                       {t('inactive')}
                    </span>
                    <span className="font-medium">{stats.warehousesByStatus.Inactive}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm cursor-pointer hover:text-orange-600" onClick={() => handleStatusClick('Maintenance', 'warehouses')}>
                    <span className="flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 text-orange-600" />
                       {t('maintenance')}
                    </span>
                    <span className="font-medium">{stats.warehousesByStatus.Maintenance}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Trips Card */}
          <Card className="cursor-pointer hover:shadow-lg transition-all border-t-4 border-t-indigo-500 hover:scale-105 duration-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">{t('activeTrips')}</CardTitle>
              <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                <Truck className="h-6 w-6 text-indigo-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats.activeTrips}</div>
              <p className="text-xs text-gray-500 mb-4 flex items-center gap-1">
                <Truck className="h-3 w-3 text-indigo-600" />
                {t('currentlyActiveTrips')}
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm cursor-pointer hover:text-yellow-600" onClick={() => handleStatusClick('Planning', 'trips')}>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3 text-yellow-600" />
                    {t('planning')}
                  </span>
                  <span className="font-medium">{stats.tripsByStatus.Planning}</span>
                </div>
                <div className="flex items-center justify-between text-sm cursor-pointer hover:text-blue-600" onClick={() => handleStatusClick('In Transit', 'trips')}>
                  <span className="flex items-center gap-1">
                    <Truck className="h-3 w-3 text-blue-600" />
                    {t('inTransit')}
                  </span>
                  <span className="font-medium">{stats.tripsByStatus['In Transit']}</span>
                </div>
                <div className="flex items-center justify-between text-sm cursor-pointer hover:text-purple-600" onClick={() => handleStatusClick('At Destination', 'trips')}>
                  <span className="flex items-center gap-1">
                    <Target className="h-3 w-3 text-purple-600" />
                    {t('atDestination')}
                  </span>
                  <span className="font-medium">{stats.tripsByStatus['At Destination']}</span>
                </div>
                <div className="flex items-center justify-between text-sm cursor-pointer hover:text-green-600" onClick={() => handleStatusClick('Completed', 'trips')}>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-green-600" />
                    {t('completed')}
                  </span>
                  <span className="font-medium">{stats.tripsByStatus.Completed}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoices Card */}
          <Card className="cursor-pointer hover:shadow-lg transition-all border-t-4 border-t-rose-500 hover:scale-105 duration-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">{t('pendingInvoices')}</CardTitle>
              <div className="h-12 w-12 rounded-full bg-rose-100 flex items-center justify-center">
                <FileText className="h-6 w-6 text-rose-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats.pendingInvoices}</div>
              <p className="text-xs text-gray-500 mb-4 flex items-center gap-1">
                <AlertCircle className="h-3 w-3 text-rose-600" />
                {t('awaitingPayment')}
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm cursor-pointer hover:text-gray-600" onClick={() => handleStatusClick('Draft', 'invoices')}>
                  <span className="flex items-center gap-1">
                    <FileText className="h-3 w-3 text-gray-600" />
                    {t('draft')}
                  </span>
                  <span className="font-medium">{stats.invoicesByStatus.Draft}</span>
                </div>
                <div className="flex items-center justify-between text-sm cursor-pointer hover:text-blue-600" onClick={() => handleStatusClick('Sent', 'invoices')}>
                  <span className="flex items-center gap-1">
                    <FileText className="h-3 w-3 text-blue-600" />
                    {t('sent')}
                  </span>
                  <span className="font-medium">{stats.invoicesByStatus.Sent}</span>
                </div>
                <div className="flex items-center justify-between text-sm cursor-pointer hover:text-green-600" onClick={() => handleStatusClick('Paid', 'invoices')}>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-green-600" />
                    {t('paid')}
                  </span>
                  <span className="font-medium">{stats.invoicesByStatus.Paid}</span>
                </div>
                <div className="flex items-center justify-between text-sm cursor-pointer hover:text-red-600" onClick={() => handleStatusClick('Overdue', 'invoices')}>
                  <span className="flex items-center gap-1">
                    <AlertCircle className="h-3 w-3 text-red-600" />
                    {t('overdue')}
                  </span>
                  <span className="font-medium">{stats.invoicesByStatus.Overdue}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Revenue Card */}
          <Card className="cursor-pointer hover:shadow-lg transition-all border-t-4 border-t-emerald-500 hover:scale-105 duration-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">{t('totalRevenue')}</CardTitle>
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">${stats.totalRevenue.toLocaleString()}</div>
              <p className="text-xs text-gray-500 mb-4 flex items-center gap-1">
                <TrendingUp className="h-3 w-3 text-emerald-600" />
                {t('totalRevenueEarned')}
              </p>
              {/* Revenue by Warehouse */}
              {Object.keys(stats.revenueByWarehouse).length > 0 && (
                <div className="space-y-2 border-t pt-3">
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    {user?.role === 'admin' ? t('revenueByWarehouseLabel') : t('revenueBreakdown')}
                  </p>
                  {Object.entries(stats.revenueByWarehouse)
                    .sort(([,a], [,b]) => b - a)
                    .map(([warehouse, revenue]) => {
                      const percentage = stats.totalRevenue > 0 ? (revenue / stats.totalRevenue * 100) : 0;
                      return (
                        <div key={warehouse} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-1">
                              <Building2 className="h-3 w-3 text-emerald-600" />
                              {warehouse}
                            </span>
                            <div className="text-right">
                              <span className="font-medium text-emerald-700">${revenue.toLocaleString()}</span>
                              <span className="text-xs text-muted-foreground ml-2">
                                {percentage.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                          {/* Progress bar */}
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-emerald-600 h-1.5 rounded-full"
                              style={{ width: `${Math.min(percentage, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Visual Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Shipment Status Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="h-5 w-5 text-blue-600" />
                {t('shipmentStatusDistribution')}
              </CardTitle>
              <CardDescription>{t('visualBreakdownAllShipments')}</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPie>
                  <Pie
                    data={[
                      { name: 'Processing', value: stats.shipmentsByStatus.Processing, color: '#eab308' },
                      { name: 'In Transit', value: stats.shipmentsByStatus['In Transit'], color: '#3b82f6' },
                      { name: 'At Destination', value: stats.shipmentsByStatus['At Destination'], color: '#8b5cf6' },
                      { name: 'Delivered', value: stats.shipmentsByStatus.Delivered, color: '#22c55e' },
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {[
                      { name: 'Processing', value: stats.shipmentsByStatus.Processing, color: '#eab308' },
                      { name: 'In Transit', value: stats.shipmentsByStatus['In Transit'], color: '#3b82f6' },
                      { name: 'At Destination', value: stats.shipmentsByStatus['At Destination'], color: '#8b5cf6' },
                      { name: 'Delivered', value: stats.shipmentsByStatus.Delivered, color: '#22c55e' },
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RechartsPie>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Invoice Status Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-emerald-600" />
                {t('invoiceStatusOverview')}
              </CardTitle>
              <CardDescription>{t('invoiceDistributionPaymentStatus')}</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={[
                    { name: 'Draft', count: stats.invoicesByStatus.Draft, color: '#6b7280' },
                    { name: 'Sent', count: stats.invoicesByStatus.Sent, color: '#3b82f6' },
                    { name: 'Paid', count: stats.invoicesByStatus.Paid, color: '#22c55e' },
                    { name: 'Overdue', count: stats.invoicesByStatus.Overdue, color: '#ef4444' },
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" radius={[8, 8, 0, 0]}>
                    {[
                      { name: 'Draft', count: stats.invoicesByStatus.Draft, color: '#6b7280' },
                      { name: 'Sent', count: stats.invoicesByStatus.Sent, color: '#3b82f6' },
                      { name: 'Paid', count: stats.invoicesByStatus.Paid, color: '#22c55e' },
                      { name: 'Overdue', count: stats.invoicesByStatus.Overdue, color: '#ef4444' },
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Recent Shipments */}
        <Card>
          <CardHeader>
            <CardTitle>{t('recentShipments')}</CardTitle>
            <CardDescription>{t('latestShipmentActivities')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentShipments.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>{t('noRecentShipments')}</p>
                </div>
              ) : (
                recentShipments.map((shipment, index) => (
                  <div
                    key={shipment.id}
                    className="flex items-center justify-between p-4 border-l-4 border-l-blue-500 bg-gradient-to-r from-blue-50 to-transparent rounded-lg hover:shadow-md transition-all"
                  >
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Package className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900">{shipment.id}</div>
                        <div className="text-sm text-gray-600 truncate">
                          <span className="font-medium">{shipment.customer}</span>
                          <span className="mx-2">→</span>
                          <span>{shipment.destination}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 flex-shrink-0">
                      <span className="text-xs text-gray-500 hidden sm:inline">{new Date(shipment.date).toLocaleDateString()}</span>
                      <Badge className={`${getStatusColor(shipment.status)} flex items-center gap-1`}>
                        {getStatusIcon(shipment.status)}
                        <span>{getStatusTranslation(shipment.status)}</span>
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section Details Dialog */}
      <Dialog open={!!activeSection} onOpenChange={() => setActiveSection(null)}>
        <DialogContent className="sm:max-w-[1000px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getSectionInfo(activeSection || '').icon}
              {getSectionInfo(activeSection || '').title}
            </DialogTitle>
            <DialogDescription>
              {activeSection?.includes('Processing') || activeSection?.includes('Transit') || activeSection?.includes('Destination') || activeSection?.includes('Delivered')
                ? t('detailedViewShipmentsStatus')
                : activeSection?.includes('customers')
                ? t('detailedViewCustomersByStatus')
                : activeSection?.includes('warehouses')
                ? t('detailedViewWarehousesByType')
                : activeSection?.includes('trips')
                ? t('detailedViewTripsByStatus')
                : activeSection?.includes('invoices')
                ? t('detailedViewInvoicesByStatus')
                : `${t('detailedViewShipmentsStatus')}`}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[70vh] overflow-y-auto">
            {sectionData.length > 0 ? (
              <div className="space-y-4">
                {(activeSection === 'customers' || activeSection?.startsWith('customers-')) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sectionData.map((customer: any) => (
                      <Card key={customer.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="font-semibold text-lg">{customer.name}</h3>
                                <p className="text-sm text-gray-600">{t('customerCode')} {customer.id}</p>
                              </div>
                               <Badge className={
                                customer.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }>
                                {getStatusTranslation(customer.status)}
                              </Badge>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex items-center text-gray-600">
                                <span className="font-medium w-20">{t('emailField')}</span>
                                <span>{customer.email}</span>
                              </div>
                              <div className="flex items-center text-gray-600">
                                <span className="font-medium w-20">{t('phoneField')}</span>
                                <span>{customer.phone}</span>
                              </div>
                              <div className="flex items-center text-gray-600">
                                <span className="font-medium w-20">{t('locationField')}</span>
                                <span>{customer.city}, {customer.country}</span>
                              </div>
                              <div className="flex items-center text-gray-600">
                                <span className="font-medium w-20">{t('shipmentsField')}</span>
                                <span>{customer.totalShipments}</span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {(activeSection === 'warehouses' || activeSection?.startsWith('warehouses-')) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {sectionData.map((warehouse: any) => (
                      <Card key={warehouse.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="font-semibold text-lg">{warehouse.name}</h3>
                                <p className="text-sm text-gray-600">{t('codeField')} {warehouse.id}</p>
                              </div>
                              <Badge className={
                                warehouse.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }>
                                {getStatusTranslation(warehouse.status)}
                              </Badge>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex items-center text-gray-600">
                                <span className="font-medium w-24">{t('locationField')}</span>
                                <span>{warehouse.location}</span>
                              </div>
                              <div className="flex items-center text-gray-600">
                                <span className="font-medium w-24">{t('addressField')}</span>
                                <span>{warehouse.address}</span>
                              </div>
                              <div className="flex items-center text-gray-600">
                                <span className="font-medium w-24">{t('managerField')}</span>
                                <span>{warehouse.manager}</span>
                              </div>
                              <div className="flex items-center text-gray-600">
                                <span className="font-medium w-24">{t('capacityField')}</span>
                                <span>{warehouse.capacity} {t('units')}</span>
                              </div>
                              <div className="flex items-center text-gray-600">
                                <span className="font-medium w-24">{t('currentField')}</span>
                                <span>{warehouse.currentStock} {t('units')}</span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {(activeSection === 'trips' || activeSection?.startsWith('trips-')) && (
                  <div className="space-y-4">
                    {sectionData.map((trip: any) => (
                      <Card key={trip.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="space-y-3">
                              <div>
                                <h3 className="font-semibold text-lg">{trip.id}</h3>
                                <p className="text-sm text-gray-600">{t('driverField')} {trip.driver}</p>
                              </div>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div className="space-y-2">
                                   <div className="flex items-center text-gray-600">
                                     <span className="font-medium w-16">{t('vehicleField')}</span>
                                     <span>{trip.vehicle}</span>
                                   </div>
                                   <div className="flex items-center text-gray-600">
                                     <span className="font-medium w-16">{t('originField')}</span>
                                     <span>{trip.origin}</span>
                                   </div>
                                   <div className="flex items-center text-gray-600">
                                     <span className="font-medium w-16">{t('weightField')}</span>
                                     <span>{trip.totalWeight} kg</span>
                                   </div>
                                 </div>
                                 <div className="space-y-2">
                                   <div className="flex items-center text-gray-600">
                                     <span className="font-medium w-16">{t('destinationField')}</span>
                                     <span>{trip.destination}</span>
                                   </div>
                                   <div className="flex items-center text-gray-600">
                                     <span className="font-medium w-16">{t('shipmentsFieldShort')}</span>
                                     <span>{trip.totalShipments}</span>
                                   </div>
                                   <div className="flex items-center text-gray-600">
                                     <span className="font-medium w-16">{t('packagesField')}</span>
                                     <span>{trip.totalPackages}</span>
                                   </div>
                                 </div>
                               </div>
                               <div className="flex items-center space-x-4 text-sm text-gray-600">
                                 <span>{t('createdField')} {trip.createdAt}</span>
                                 <span>{t('estArrival')} {trip.estimatedArrival}</span>
                               </div>
                            </div>
                            <div className="text-right">
                              <Badge className={
                                trip.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                trip.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                                'bg-red-100 text-red-800'
                              }>
                                {getStatusTranslation(trip.status)}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {(activeSection === 'invoices' || activeSection?.startsWith('invoices-')) && (
                  <div className="space-y-4">
                     {sectionData.map((invoice: any) => (
                       <Card key={invoice.id} className="hover:shadow-md transition-shadow">
                         <CardContent className="p-4">
                           <div className="flex justify-between items-center">
                             <div className="space-y-2">
                               <h3 className="font-semibold text-lg">{invoice.id}</h3>
                                <div className="flex items-center space-x-4 text-sm text-gray-600">
                                  <span>{t('customerField')} {invoice.customer}</span>
                                  <span>{t('dueField')} {invoice.dueDate || t('notAvailable')}</span>
                                  <span>{t('createdField')} {invoice.createdAt || t('notAvailable')}</span>
                                </div>
                             </div>
                             <div className="text-right space-y-2">
                               <div className="text-2xl font-bold">${(typeof invoice.amount === 'number' ? invoice.amount : parseFloat(invoice.amount) || 0).toLocaleString()}</div>
                                <Badge className={
                                  invoice.status === 'Paid' ? 'bg-green-100 text-green-800' : 
                                  invoice.status === 'Sent' ? 'bg-blue-100 text-blue-800' :
                                  invoice.status === 'Overdue' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }>
                                  {getStatusTranslation(invoice.status)}
                                </Badge>
                             </div>
                           </div>
                         </CardContent>
                       </Card>
                     ))}
                  </div>
                )}

                {(activeSection === 'Processing' || activeSection === 'In Transit' || activeSection === 'At Destination' || activeSection === 'Delivered') && (
                  <div className="space-y-4">
                    {sectionData.map((shipment: any) => (
                      <Card key={shipment.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="space-y-3">
                              <div>
                                <h3 className="font-semibold text-lg">{shipment.id}</h3>
                                <p className="text-sm text-gray-600">{t('customerField')} {shipment.customer} ({shipment.customerCode})</p>
                              </div>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div className="space-y-2">
                                   <div className="flex items-center text-gray-600">
                                     <span className="font-medium w-16">{t('tripField')}</span>
                                     <span>{shipment.tripName || t('unassigned')}</span>
                                   </div>
                                   <div className="flex items-center text-gray-600">
                                     <span className="font-medium w-16">{t('weightField')}</span>
                                     <span>{shipment.weight} kg</span>
                                   </div>
                                   <div className="flex items-center text-gray-600">
                                     <span className="font-medium w-16">{t('volumeField')}</span>
                                     <span>{shipment.volume} m³</span>
                                   </div>
                                 </div>
                                 <div className="space-y-2">
                                   <div className="flex items-center text-gray-600">
                                     <span className="font-medium w-16">{t('destinationField')}</span>
                                     <span>{shipment.destination}</span>
                                   </div>
                                   <div className="flex items-center text-gray-600">
                                     <span className="font-medium w-16">{t('packagesField')}</span>
                                     <span>{shipment.packages}</span>
                                   </div>
                                   <div className="flex items-center text-gray-600">
                                     <span className="font-medium w-16">{t('freightField')}</span>
                                    <Badge className={
                                      shipment.freightMethod === 'Air' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                    }>
                                      {shipment.freightMethod}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                               <div className="flex items-center space-x-4 text-sm text-gray-600">
                                 <span>{t('createdField')} {shipment.createdAt}</span>
                                 <span>{t('estDelivery')} {shipment.estimatedDelivery}</span>
                               </div>
                            </div>
                            <div className="text-right">
                              <Badge className={getStatusColor(shipment.status)}>
                                {getStatusTranslation(shipment.status)}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('noItemsFound')}</h3>
                <p className="text-gray-500">
                  {t('noItemsFoundInSystem')}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <div className="flex justify-between items-center w-full">
              <div className="text-sm text-gray-500">
                {t('showing')} {sectionData.length}
              </div>
              <Button onClick={() => setActiveSection(null)}>
                {t('close')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}